import type { ChatChannel, ChatMessage } from '~/types/chat.model'

export const useChatStore = defineStore('chatStore', () => {
  const channels = ref<ChatChannel[]>([])
  const messages = ref<ChatMessage[]>([])
  const selectedChannel = ref<ChatChannel | null>(null)
  const messagesContainer = ref<HTMLElement | null>(null)
  const unreadCount = ref(0)
  const userStore = useUserStore()
  const isLoadingMessages = ref(false)
  const hasMoreMessages = ref(true)
  const currentPage = ref(1)
  const messagesPerPage = 20

  const scrollToBottom = () => {
    nextTick(() => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    })
  }

  const loadMessages = async (channelId: number | string, page = 1, append = false) => {
    if (isLoadingMessages.value) return

    isLoadingMessages.value = true
    try {
      const response = await useApi(
        `chat/messages?filter[channel_id]=${channelId}&page=${page}&per_page=${messagesPerPage}&sort=-created_at`,
        'GET'
      )

      const newMessages = response.data || []

      if (append) {
        // For pagination, prepend older messages (they come newest first from API due to sort=-created_at)
        const existingIds = new Set(messages.value.map((m) => m.id))
        const uniqueNewMessages = newMessages.filter((msg: ChatMessage) => !existingIds.has(msg.id))
        messages.value = [...uniqueNewMessages.reverse(), ...messages.value]
      } else {
        // Initial load - reverse to show oldest first, newest last
        messages.value = [...newMessages].reverse()
        currentPage.value = 1
        scrollToBottom()
      }

      // Check if there are more messages to load
      hasMoreMessages.value = newMessages.length === messagesPerPage
      currentPage.value = page
    } catch {
    } finally {
      isLoadingMessages.value = false
    }
  }

  const selectChannel = (channel: ChatChannel | null) => {
    if (selectedChannel.value && (channel === null || selectedChannel.value.id !== channel.id)) {
      messages.value = []
      hasMoreMessages.value = true
      currentPage.value = 1
    }
    selectedChannel.value = channel

    if (channel) {
      loadMessages(channel.id)
      // Reset unread count when viewing a channel
      markAsRead()
    }
  }

  const loadChannels = async () => {
    const response = await useApi('chat/channels?include=messages,participants', 'GET')
    channels.value = response?.data || []
  }

  const sendMessage = async (text: string) => {
    if (!selectedChannel.value || !text.trim()) return

    const response = await useApi('chat/messages', 'POST', {
      channel_id: selectedChannel.value.id,
      message: text.trim()
    })

    if (response?.data) {
      // Insert message in chronological order
      const newMessage = response.data
      const insertIndex = messages.value.findIndex(
        (msg) => new Date(msg.created_at) > new Date(newMessage.created_at)
      )
      if (insertIndex === -1) {
        messages.value.push(newMessage)
      } else {
        messages.value.splice(insertIndex, 0, newMessage)
      }
      scrollToBottom()
    }
  }

  const getOrCreateChannelWithUser = async (userId: number): Promise<ChatChannel> => {
    if (!channels.value.length) await loadChannels()

    const currentUserId = userStore.user.id

    const targetUserIds = [currentUserId, userId]

    const targetUserIdsSet = new Set(targetUserIds)

    const existingChannel = channels.value.find((channel) => {
      const participantIds = channel.participants || []
      return participantIds.length === 2 && participantIds.every((id) => targetUserIdsSet.has(id))
    })

    if (existingChannel) {
      return existingChannel
    }

    const channelResponse = await useApi('chat/channels', 'POST', {
      is_group_channel: false,
      participants: targetUserIds
    })

    const createdChannel = channelResponse.data

    channels.value.push(createdChannel)

    return createdChannel
  }

  const addChannel = (channel: ChatChannel) => {
    const existingIndex = channels.value.findIndex((c) => c.id === channel.id)
    if (existingIndex >= 0) {
      channels.value[existingIndex] = channel
    } else {
      channels.value.unshift(channel)
    }

    // Trigger reactivity for websocket plugin to subscribe to new channel
    channels.value = [...channels.value]
  }

  const addMessage = (message: ChatMessage, fromWebsocket = false) => {
    // Check if message already exists to prevent duplicates
    const messageExists = messages.value.find((m) => m.id === message.id)
    if (messageExists) return

    // Only add if we're viewing the correct channel
    if (selectedChannel.value && message.channel_id === selectedChannel.value.id) {
      // Insert message in chronological order
      const insertIndex = messages.value.findIndex(
        (msg) => new Date(msg.created_at) > new Date(message.created_at)
      )
      if (insertIndex === -1) {
        messages.value.push(message)
      } else {
        messages.value.splice(insertIndex, 0, message)
      }
      scrollToBottom()
    }

    // If message is from websocket and from another user, increment unread count and trigger toast
    if (fromWebsocket && message.user_id !== userStore.user.id) {
      // Only increment unread count if we're not viewing the channel or chat panel is closed
      if (!selectedChannel.value || message.channel_id !== selectedChannel.value.id) {
        unreadCount.value++
      }

      // Emit event for toast notification (to be handled at app level)
      emitMessageNotification(message)
    }
  }

  const loadMoreMessages = async () => {
    if (!selectedChannel.value || !hasMoreMessages.value || isLoadingMessages.value) return

    const nextPage = currentPage.value + 1
    await loadMessages(selectedChannel.value.id, nextPage, true)
  }

  const markAsRead = () => {
    unreadCount.value = 0
  }

  const emitMessageNotification = (message: ChatMessage) => {
    // Find the channel name
    const channel = channels.value.find((c) => c.id === message.channel_id)
    const channelName = channel?.subject || 'Chat'

    // Emit custom event that can be caught by components with toast access
    if (process.client) {
      window.dispatchEvent(
        new CustomEvent('chatMessageReceived', {
          detail: {
            message,
            channelName,
            summary: 'Neue Nachricht',
            detail: `Nachricht in ${channelName}`
          }
        })
      )
    }
  }

  return {
    channels,
    messages,
    selectedChannel,
    messagesContainer,
    unreadCount,
    isLoadingMessages,
    hasMoreMessages,
    loadChannels,
    loadMessages,
    loadMoreMessages,
    selectChannel,
    sendMessage,
    scrollToBottom,
    getOrCreateChannelWithUser,
    addChannel,
    addMessage,
    markAsRead
  }
})
